import cloneDeep from 'lodash/cloneDeep';
import { create } from 'zustand';
import { getState, useState } from '@/framework';
import type { IExportState, ILayerProps, StateType } from '@/version-types';

/** 状态 */
export interface IState {
    /** 原始图层信息 */
    originLayer: ILayerProps | null;

    currentLayer: ILayerProps | null;

    isWasmReady: boolean;

    applyLoading: boolean;

    rendering: boolean;
}

/** 动作 */
export interface IAction {
    init: (layer: ILayerProps, currentLayer: ILayerProps) => void;
    setCurrentLayer: (layer: ILayerProps | null) => void;
    setIsWasmReady: (isWasmReady: boolean) => void;
    setApplyLoading: (applyLoading: boolean) => void;
    setRendering: (rendering: boolean) => void;
    destroy: () => void;
}

/** 存储类型 */
export interface IStore extends IState, IAction {}

/** 初始化状态 */
function getInitialState(): IState {
    return {
        originLayer: null,
        currentLayer: null,
        isWasmReady: false,
        applyLoading: false,
        rendering: false,
    };
}

/** 创建存储 */
const createState = (_identity: string) => {
    const store = create<IStore>((set) => ({
        ...getInitialState(),

        init(layer, currentLayer) {
            set({
                originLayer: cloneDeep(layer),
                currentLayer: cloneDeep(currentLayer),
                isWasmReady: false,
                applyLoading: false,
                rendering: false,
            });
        },

        setCurrentLayer(layer) {
            set({
                currentLayer: layer ? cloneDeep(layer) : null,
            });
        },

        setIsWasmReady(isWasmReady) {
            set({ isWasmReady });
        },

        setApplyLoading(applyLoading) {
            set({ applyLoading });
        },

        setRendering(rendering) {
            set({ rendering });
        },

        destroy() {
            set(getInitialState());
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'image-effect-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useImageEffectState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getImageEffectState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

export const watchImageEffectState = (identity: string, callback: (state: IStore) => void) => {
    return getState<ModuleType>(identity)[name].subscribe((state, preState) => {
        if (JSON.stringify(preState) !== JSON.stringify(state)) {
            callback(state);
        }
    });
};

export const useOriginLayer = () => {
    return useImageEffectState()((state) => state.originLayer);
};

export const useIsWasmReady = () => {
    return useImageEffectState()((state) => state.isWasmReady);
};

export const useApplyLoading = () => {
    return useImageEffectState()((state) => state.applyLoading);
};

export const useRendering = () => {
    return useImageEffectState()((state) => state.rendering);
};

export const setApplyLoading = (identity: string, applyLoading: boolean) => {
    return getState<ModuleType>(identity)[name].setState({
        applyLoading: applyLoading,
    });
};

export const setIsWasmReady = (identity: string, isWasmReady: boolean) => {
    return getState<ModuleType>(identity)[name].setState({
        isWasmReady: isWasmReady,
    });
};

export const setCurrentLayer = (identity: string, layer: ILayerProps) => {
    return getState<ModuleType>(identity)[name].setState({
        currentLayer: layer,
    });
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
